import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Plug, Copy, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, timeAgo, copyOrWarn } from "@/lib/api";
import { ConnBars } from "@/components/ConnBars";

const joinUrl = (code) => `${window.location.origin}/join/${code}`;
const invite = (s) => `Bonjour, pour l'assistance à distance, rendez-vous sur ${joinUrl(s.code)} (ou ${window.location.origin}/join avec le code ${s.code}) puis lancez le fichier téléchargé.`;
const fmtCode = (c) => `${c.slice(0, 3)} ${c.slice(3)}`;
const FILTERS = { all: () => true, waiting: (s) => s.status === "waiting", connected: (s) => s.status === "connected" };

const NewSessionDialog = ({ open, onOpenChange, onCreated }) => {
  const [name, setName] = useState("");
  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/support", { name });
      setName("");
      onOpenChange(false);
      onCreated(data);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-support-dialog">
        <DialogHeader><DialogTitle>Nouvelle session d'assistance</DialogTitle></DialogHeader>
        <form onSubmit={create}>
          <Input autoFocus data-testid="support-name-input" placeholder="Nom du client (ex. Mme Martin – imprimante)" value={name} onChange={(e) => setName(e.target.value)} />
          <DialogFooter className="mt-4"><Button type="submit" disabled={!name.trim()} data-testid="support-create-btn">Créer la session</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const SupportDetail = ({ s }) => {
  if (!s) return <section className="w-[520px] flex-shrink-0 grid place-items-center text-slate-400 text-sm">Sélectionnez une session</section>;
  const copy = (t) => copyOrWarn(t);
  return (
    <section className="w-[520px] flex-shrink-0 overflow-y-auto" data-testid="support-detail">
      <div className="h-[72px] flex items-center px-6 border-b border-slate-200"><h2 className="text-[22px] truncate">{s.name}</h2></div>
      <div className="p-6 space-y-6">
        <div className="rounded-lg border border-slate-200 p-6 text-center">
          <p className="text-sm text-slate-600 mb-2">Code à communiquer au client</p>
          <p className="font-mono text-5xl font-semibold tracking-[0.2em] text-blue-700" data-testid="support-code">{fmtCode(s.code)}</p>
          <p className="text-xs text-slate-500 mt-3">Valable 2 heures tant que le client ne s'est pas connecté.</p>
        </div>
        <div>
          <p className="text-sm font-semibold mb-2">Lien d'invitation</p>
          <div className="flex gap-2">
            <Input readOnly value={joinUrl(s.code)} data-testid="support-join-link" />
            <Button variant="secondary" onClick={() => copy(joinUrl(s.code))} data-testid="copy-join-link-btn"><Copy className="h-4 w-4" /></Button>
          </div>
        </div>
        <div>
          <p className="text-sm font-semibold mb-2">Message à envoyer</p>
          <p className="text-sm bg-slate-50 border border-slate-200 rounded p-3">{invite(s)}</p>
          <Button variant="secondary" size="sm" className="mt-2" onClick={() => copy(invite(s))} data-testid="copy-invite-btn">Copier le message</Button>
        </div>
        {s.machine && (
          <div className="text-sm space-y-1" data-testid="support-machine-info">
            <p className="font-semibold">Poste du client</p>
            <p>{s.machine.hostname} · {s.machine.os_version}</p>
            <p>Utilisateur : {s.machine.username} · IP : {s.machine.ip_public}</p>
          </div>
        )}
        <p className="text-xs text-slate-500">À la fin de la session, rien ne reste installé sur le poste du client.</p>
      </div>
    </section>
  );
};

export default function Support() {
  const { billing } = useOutletContext();
  const [sessions, setSessions] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selId, setSelId] = useState(null);
  const [open, setOpen] = useState(false);
  const canConnect = billing?.state !== "expired";

  const load = useCallback(() => api.get("/support").then(({ data }) => setSessions(data)), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  const list = sessions.filter(FILTERS[filter]);
  const sel = sessions.find((s) => s.support_id === selId);
  const join = () => sel?.machine?.online && window.open(`/session/${sel.machine.machine_id}`, "_blank");
  const end = async () => {
    if (!window.confirm("Terminer la session ? L'outil sera fermé chez le client.")) return;
    await api.delete(`/support/${sel.support_id}`);
    setSelId(null);
    load();
  };
  const count = (k) => sessions.filter(FILTERS[k]).length;
  const Item = ({ k, label }) => (
    <div onClick={() => setFilter(k)} data-testid={`support-filter-${k}`} className={`flex justify-between px-6 py-2.5 cursor-pointer text-[17px] ${filter === k ? "bg-slate-200/80 font-semibold" : "hover:bg-slate-100"}`}>
      <span>{label}</span><span className="text-slate-600">{count(k)}</span>
    </div>
  );

  return (
    <>
      <section className="w-[340px] flex-shrink-0 border-r border-slate-200" data-testid="support-column">
        <div className="px-6 pt-6 pb-4">
          <h1 className="text-3xl font-medium mb-3">Support</h1>
          <p className="text-[15px] text-slate-700 mb-5">Créez une session ponctuelle : le client saisit le code, rien ne reste installé ensuite.</p>
          <Button data-testid="new-support-btn" disabled={!canConnect} onClick={() => setOpen(true)} className="w-full h-11 text-[16px] font-semibold bg-[#2563EB] hover:bg-[#1D4ED8] rounded">Générer +</Button>
        </div>
        <Item k="all" label="Toutes les sessions" />
        <Item k="waiting" label="En attente du client" />
        <Item k="connected" label="Client connecté" />
      </section>
      <section className="flex-1 min-w-[460px] border-r border-slate-200 flex flex-col min-h-0">
        <div className="h-[72px] flex items-center gap-6 px-5 border-b border-slate-200">
          <h2 className="text-[22px]">Sessions</h2>
          <button data-testid="support-join-btn" disabled={!sel?.machine?.online || !canConnect} onClick={join} className="flex items-center gap-1.5 text-[17px] hover:text-blue-600 disabled:text-slate-300"><Plug className="h-5 w-5" strokeWidth={1.6} /> Participer</button>
          <button data-testid="support-copy-btn" disabled={!sel} onClick={() => copyOrWarn(invite(sel), "Invitation copiée")} className="flex items-center gap-1.5 text-[17px] hover:text-blue-600 disabled:text-slate-300"><Copy className="h-5 w-5" strokeWidth={1.6} /> Inviter</button>
          <button data-testid="support-end-btn" disabled={!sel} onClick={end} className="flex items-center gap-1.5 text-[17px] hover:text-red-600 disabled:text-slate-300"><XCircle className="h-5 w-5" strokeWidth={1.6} /> Terminer</button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {list.map((s) => (
            <div key={s.support_id} data-testid={`support-row-${s.support_id}`} onClick={() => setSelId(s.support_id)} onDoubleClick={join} className={`flex items-center gap-4 px-5 py-3 border-b border-slate-200 cursor-pointer ${s.support_id === selId ? "bg-slate-100" : "hover:bg-slate-50"}`}>
              <input type="checkbox" readOnly checked={s.support_id === selId} className="h-5 w-5 accent-blue-600" />
              <div className="flex-1 min-w-0 leading-snug">
                <p className="text-[19px] truncate">{s.name}</p>
                <p className="text-[13px] text-slate-700">Code : <span className="font-mono">{fmtCode(s.code)}</span></p>
                <p className="text-[13px] text-slate-700">Créée par {s.created_by} {timeAgo(s.created_at)}</p>
                <p className="text-[13px] text-slate-700">{s.machine ? `Client : ${s.machine.username} sur ${s.machine.hostname}` : "En attente du client…"}</p>
              </div>
              <ConnBars m={s.machine || { machine_id: s.support_id }} />
            </div>
          ))}
          {!list.length && <p className="p-10 text-center text-slate-500" data-testid="support-empty">Aucune session. Cliquez sur « Générer + ».</p>}
        </div>
      </section>
      <SupportDetail s={sel} />
      <NewSessionDialog open={open} onOpenChange={setOpen} onCreated={(s) => { load(); setSelId(s.support_id); }} />
    </>
  );
}
